import { z } from 'zod';
import { Agent, TimestampMs } from './common.js';
import { CiphertextBox, ContentBase64Url, EncryptedContent } from './content.js';
import { ApprovalDecision, ApprovalKind, EVENT_KINDS, MarkerType, RateLimitWindowName, RunStatus, TurnOutcome, } from './events.js';
import { SignedCommand, WebAuthnAssertion } from './signing.js';
/**
 * DRAFT Protocol 1: end-to-end encrypted content (docs/e2e.md). The hub stores and relays only the
 * plaintext METADATA declared here plus `Sealed` ciphertext; every schema on the plaintext side is
 * strict, so an unknown (possibly content-bearing) field fails closed instead of being stored.
 * These shapes are staged only: endpoint routing verification and durable signed-directory
 * checkpoint integration are required before any relay route may use them.
 *
 * A `Sealed` value is `@viko/crypto` `sealContent` over canonical JSON. Its authenticated context
 * is `{version: 1, accountId, epoch, objectId: ref.id, runId: ref.ns, seq: ref.rev, kind: ref.kind}`
 * where `ref` is fixed by where the value lives (defined by each staged transport contract). Readers always
 * rebuild the context from their own expectation, never from the stored record alone.
 */
const Counter = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** Opaque ids only: bounded, no whitespace. User text never goes in an id. */
export const OpaqueId = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\x21-\x7e]+$/, 'opaque id');
/** One sealed value (per-object HKDF key, AES-256-GCM, padded to 256-byte buckets). */
export const Sealed = z.strictObject({
    epoch: Counter,
    box: CiphertextBox,
});
/** Where a sealed value lives: its authenticated context minus account and epoch. */
export const SealRef = z.strictObject({
    ns: OpaqueId,
    id: OpaqueId,
    rev: Counter,
    kind: z.string().min(1).max(64),
});
/** A sealed value that the hub copied from another record (cards referencing their source). */
export const SealedRef = z.strictObject({ ref: SealRef, sealed: Sealed });
// ---------------------------------------------------------------------------------------------
// Transcript events
// ---------------------------------------------------------------------------------------------
/**
 * The only event fields the hub may read: ids, enums, numeric counters. Derived by the producer
 * with `deriveEventMeta`; every reader re-derives it from the decrypted event and rejects a
 * mismatch, so the projection is authenticated end to end.
 */
export const EventMeta = z.strictObject({
    /** asst.delta / asst.block: the hub drops superseded deltas. */
    blockId: OpaqueId.optional(),
    /** status */
    state: RunStatus.optional(),
    /** turn.end */
    outcome: TurnOutcome.optional(),
    /** question / question.answered */
    questionId: OpaqueId.optional(),
    blocking: z.boolean().optional(),
    /** approval / approval.resolved */
    approvalId: OpaqueId.optional(),
    approvalKind: ApprovalKind.optional(),
    decision: ApprovalDecision.optional(),
    /** question.answered / approval.resolved */
    by: z.enum(['user', 'auto', 'timeout', 'cancelled', 'policy', 'cli']).optional(),
    /** artifact */
    artifactId: OpaqueId.optional(),
    thumbArtifactId: OpaqueId.optional(),
    /** usage */
    contextPct: z.number().min(0).max(100).optional(),
    costUsd: z.number().nonnegative().optional(),
    /** ratelimit (numbers and enums only; native names and plan text stay sealed) */
    windows: z
        .array(z.strictObject({
        window: RateLimitWindowName,
        usedPct: z.number().min(0).optional(),
        resetsAt: TimestampMs.optional(),
        status: z.enum(['allowed', 'warning', 'rejected']).optional(),
        windowMinutes: z.number().int().positive().optional(),
    }))
        .max(16)
        .optional(),
    /** error */
    fatal: z.boolean().optional(),
    retrying: z.boolean().optional(),
    /** marker (+ `ref`: the predecessor run for `continued_from`) */
    marker: MarkerType.optional(),
    ref: OpaqueId.optional(),
    /** notice: the only actionable notice code the hub reacts to */
    notice: z.enum(['rotation_pending']).optional(),
    /** run.start */
    resumed: z.boolean().optional(),
});
const OPAQUE = /^[\x21-\x7e]{1,200}$/;
const opaque = (v) => typeof v === 'string' && OPAQUE.test(v) ? v : undefined;
/**
 * The plaintext projection of an event. Pure and deterministic: producer and reader compute the
 * same object from the same event (compare with `canonicalize`).
 */
export function deriveEventMeta(e) {
    const m = {};
    const ev = e;
    switch (ev.kind) {
        case 'asst.delta':
        case 'asst.block': {
            const b = opaque(ev.blockId);
            if (b)
                m.blockId = b;
            break;
        }
        case 'status':
            if (RunStatus.safeParse(ev.state).success)
                m.state = ev.state;
            break;
        case 'turn.end':
            if (TurnOutcome.safeParse(ev.outcome).success)
                m.outcome = ev.outcome;
            break;
        case 'question': {
            const q = opaque(ev.questionId);
            if (q)
                m.questionId = q;
            m.blocking = ev.blocking !== false;
            break;
        }
        case 'question.answered': {
            const q = opaque(ev.questionId);
            if (q)
                m.questionId = q;
            if (ev.by === 'user' || ev.by === 'auto' || ev.by === 'timeout' || ev.by === 'cancelled')
                m.by = ev.by;
            break;
        }
        case 'approval': {
            const a = opaque(ev.approvalId);
            if (a)
                m.approvalId = a;
            if (ApprovalKind.safeParse(ev.approvalKind).success)
                m.approvalKind = ev.approvalKind;
            break;
        }
        case 'approval.resolved': {
            const a = opaque(ev.approvalId);
            if (a)
                m.approvalId = a;
            if (ApprovalDecision.safeParse(ev.decision).success)
                m.decision = ev.decision;
            if (ev.by === 'user' || ev.by === 'auto' || ev.by === 'policy' || ev.by === 'cli')
                m.by = ev.by;
            break;
        }
        case 'artifact': {
            const a = opaque(ev.artifactId);
            if (a)
                m.artifactId = a;
            const t = opaque(ev.thumbArtifactId);
            if (t)
                m.thumbArtifactId = t;
            break;
        }
        case 'usage':
            if (typeof ev.contextPct === 'number' && ev.contextPct >= 0 && ev.contextPct <= 100)
                m.contextPct = ev.contextPct;
            if (typeof ev.costUsd === 'number' && ev.costUsd >= 0)
                m.costUsd = ev.costUsd;
            break;
        case 'ratelimit':
            if (Array.isArray(ev.windows)) {
                m.windows = ev.windows.slice(0, 16).flatMap((w) => {
                    if (!RateLimitWindowName.safeParse(w.window).success)
                        return [];
                    const out = {
                        window: w.window,
                    };
                    if (typeof w.usedPct === 'number' && w.usedPct >= 0)
                        out.usedPct = w.usedPct;
                    if (Number.isSafeInteger(w.resetsAt) && w.resetsAt >= 0)
                        out.resetsAt = w.resetsAt;
                    if (w.status === 'allowed' || w.status === 'warning' || w.status === 'rejected')
                        out.status = w.status;
                    if (Number.isSafeInteger(w.windowMinutes) && w.windowMinutes > 0)
                        out.windowMinutes = w.windowMinutes;
                    return [out];
                });
            }
            break;
        case 'error':
            if (ev.fatal === true)
                m.fatal = true;
            if (ev.retrying === true)
                m.retrying = true;
            break;
        case 'marker': {
            if (MarkerType.safeParse(ev.marker).success)
                m.marker = ev.marker;
            const r = ev.marker === 'continued_from' ? opaque(ev.ref) : undefined;
            if (r)
                m.ref = r;
            break;
        }
        case 'notice':
            if (ev.code === 'rotation_pending' && ev.action)
                m.notice = 'rotation_pending';
            break;
        case 'run.start':
            if (ev.resumed === true)
                m.resumed = true;
            break;
    }
    return m;
}
/**
 * One transcript event on the wire and in hub storage. The whole normalized `TranscriptEvent`
 * (including its envelope) is inside `sealed`, at ref `{ns: runId, id: eventId, rev: seq, kind}`.
 */
export const SealedEvent = z.strictObject({
    runId: OpaqueId,
    seq: Counter,
    eventId: OpaqueId,
    ts: TimestampMs,
    kind: z.enum(EVENT_KINDS),
    agent: Agent,
    meta: EventMeta,
    sealed: Sealed,
});
// ---------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------
/**
 * Plaintext routing next to an encrypted SignedCommand payload (the payload is `{context, box}`
 * from `signEncryptedCommand`). Not signed: the daemon decrypts and rejects the command
 * (`payload-mismatch`) when any route field differs from the decrypted payload.
 */
export const CommandRoute = z.strictObject({
    runId: OpaqueId.optional(),
    projectId: OpaqueId.optional(),
    workstreamId: OpaqueId.optional(),
    taskId: OpaqueId.optional(),
    requestId: OpaqueId.optional(),
    agent: Agent.optional(),
    questionId: OpaqueId.optional(),
    approvalId: OpaqueId.optional(),
});
/** Staged ciphertext command; the relay projection must be checked after decryption. */
export const SignedCommandV1 = SignedCommand.extend({
    payload: EncryptedContent,
    route: CommandRoute.optional(),
}).strict();
export const COMMAND_ROUTE_KEYS = [
    'runId',
    'projectId',
    'workstreamId',
    'taskId',
    'requestId',
    'agent',
    'questionId',
    'approvalId',
];
/** The routing projection of a plaintext command payload (producer side). */
export function deriveCommandRoute(payload) {
    const out = {};
    if (!payload || typeof payload !== 'object')
        return out;
    const p = payload;
    for (const k of COMMAND_ROUTE_KEYS) {
        const v = p[k];
        if (k === 'agent') {
            if (v === 'claude' || v === 'codex')
                out.agent = v;
        }
        else {
            const o = opaque(v);
            if (o)
                out[k] = o;
        }
    }
    return out;
}
// ---------------------------------------------------------------------------------------------
// Keys (docs/e2e.md; @viko/crypto account-keys.ts). Public-key material and ciphertext only.
// ---------------------------------------------------------------------------------------------
const P256Jwk = z.strictObject({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    y: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
});
export const ContentRecipientSchema = z.strictObject({
    id: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    type: z.enum(['device', 'daemon', 'recovery', 'prf']),
    publicKey: P256Jwk,
    ref: OpaqueId.optional(),
});
export const KeyEpochStatementSchema = z.strictObject({
    v: z.literal(1),
    type: z.literal('viko.key-epoch'),
    accountId: OpaqueId,
    epoch: Counter,
    keyCommitment: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    recipients: z.array(ContentRecipientSchema).min(1).max(1024),
    issuedAt: TimestampMs,
});
export const SignedKeyEpochSchema = z.strictObject({
    statement: KeyEpochStatementSchema,
    assertion: WebAuthnAssertion,
});
export const SignedContentRecipientSchema = z.strictObject({
    statement: z.strictObject({
        v: z.literal(1),
        type: z.literal('viko.content-recipient'),
        accountId: OpaqueId,
        recipient: ContentRecipientSchema,
        issuedAt: TimestampMs,
    }),
    assertion: WebAuthnAssertion,
});
export const KeyWrapSchema = z.strictObject({
    v: z.literal(1),
    accountId: OpaqueId,
    recipientId: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    ephemeral: P256Jwk,
    box: CiphertextBox.refine((b) => b.ciphertext.length <= 1_000_000, 'key box too large'),
});
export const KeyEscrowSchema = z.strictObject({
    v: z.literal(1),
    accountId: OpaqueId,
    kind: z.enum(['prf', 'recovery']),
    recipientId: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    credentialId: OpaqueId.optional(),
    box: CiphertextBox.refine((b) => b.ciphertext.length <= 200_000, 'key box too large'),
});
/** Daemon content key bound to its identity key (sent in enrollment begin/claim). */
export const DaemonContentKeySchema = z.strictObject({
    publicKey: P256Jwk,
    /** Daemon identity signature under `viko/daemon-content-key/v1` over `daemonContentKeyBinding`. */
    signature: z.string().min(1),
});
/** GET /api/keys → the account's public key directory (verify everything on the endpoint). */
export const KeyDirectoryResponse = z.strictObject({
    epochs: z.array(SignedKeyEpochSchema),
    recipients: z.array(SignedContentRecipientSchema),
    escrows: z.array(KeyEscrowSchema),
    /** Wraps addressed to `?recipientId=` (empty when not requested). */
    wraps: z.array(KeyWrapSchema),
    /** Recipients (ids) that joined but still lack a wrap of the current epoch. */
    pending: z.array(z.string()),
});
/** POST /api/keys/genesis (setup scope): the first epoch, its wraps and escrows. */
export const KeyGenesisRequest = z.strictObject({
    epoch: SignedKeyEpochSchema,
    wraps: z.array(KeyWrapSchema).min(1).max(1024),
    escrows: z.array(KeyEscrowSchema).max(16),
});
/** POST /api/keys/rotate: epoch N+1 (compare-and-swap on the current epoch). */
export const KeyRotateRequest = z.strictObject({
    epoch: SignedKeyEpochSchema,
    wraps: z.array(KeyWrapSchema).min(1).max(1024),
    escrows: z.array(KeyEscrowSchema).max(16).optional(),
});
/** POST /api/keys/recipients: a joining device (passkey-signed). */
export const KeyJoinRequest = z.strictObject({ recipient: SignedContentRecipientSchema });
/** POST /api/keys/wraps: an existing endpoint wraps epochs to an approved recipient. */
export const KeyWrapsRequest = z.strictObject({ wraps: z.array(KeyWrapSchema).min(1).max(1024) });
/** POST /api/keys/escrows: add/replace a PRF or recovery escrow. */
export const KeyEscrowRequest = z.strictObject({ escrow: KeyEscrowSchema });
/**
 * hub → daemon after `welcome` and on change: signed epochs and the wraps addressed to this
 * daemon's content key. The daemon verifies each epoch against its pinned passkeys.
 */
export const KeysSyncFrame = z.strictObject({
    type: z.literal('keys.sync'),
    epochs: z.array(SignedKeyEpochSchema),
    wraps: z.array(KeyWrapSchema),
});
// ---------------------------------------------------------------------------------------------
// Grant dispatch: device-prepared, device-signed, encrypted task prompts
// ---------------------------------------------------------------------------------------------
/**
 * The PWA composes a task's prompt (`composeTaskPrompt`) from decrypted task/workstream content,
 * seals it at ref `{ns:'dispatch', id, rev:1, kind:'dispatch'}` and signs the statement with its
 * device key. The hub schedules by ids and attaches it to `hub.dispatch`; the daemon verifies the
 * device (TrustStore) and the ids before decrypting. The hub never authors a prompt.
 */
export const PreparedPromptStatement = z.strictObject({
    v: z.literal(1),
    type: z.literal('viko.prepared-prompt'),
    id: OpaqueId,
    taskId: OpaqueId,
    projectId: OpaqueId,
    workstreamId: OpaqueId.optional(),
    agent: Agent.optional(),
    deviceKeyId: OpaqueId,
    /** base64url SHA-256 over domain-separated canonical `sealed`. */
    sealedHash: ContentBase64Url.refine((s) => s.length === 43, '256-bit canonical value required'),
    issuedAt: TimestampMs,
    expiresAt: TimestampMs,
});
export const PreparedPrompt = z.strictObject({
    statement: PreparedPromptStatement,
    sealed: Sealed,
    /** Device signature (raw r||s, base64url) under `viko/prepared-prompt/v1`. */
    signature: z.string().min(1),
});
/** Sealed content of a prepared prompt. */
export const PreparedPromptContent = z.looseObject({ prompt: z.string().max(1_000_000) });
export const PREPARED_PROMPT_MAX_TTL_MS = 30 * 86_400_000;
// ---------------------------------------------------------------------------------------------
// Decrypted content schemas (inside `Sealed`; loose so new optional fields stay additive)
// ---------------------------------------------------------------------------------------------
export const ProjectContent = z.looseObject({
    name: z.string(),
    yaml: z.string().optional(),
    config: z.unknown().optional(),
});
export const WorkstreamContent = z.looseObject({
    name: z.string(),
    brief: z.string().optional(),
    branch: z.string().optional(),
    worktree: z.string().optional(),
});
export const TaskContent = z.looseObject({
    title: z.string(),
    description: z.string().optional(),
    acceptance: z.string().optional(),
    rationale: z.string().optional(),
    blockedReason: z.string().optional(),
    /** Free labels (routing labels are plaintext `labels` metadata). */
    labels: z.array(z.string()).optional(),
    note: z.string().optional(),
});
export const RuleContent = z.looseObject({
    text: z.string(),
    evidence: z.unknown().optional(),
});
export const IdeaContent = z.looseObject({
    text: z.string(),
    attachments: z
        .array(z.looseObject({ artifactId: z.string(), name: z.string(), mime: z.string().optional() }))
        .optional(),
});
export const CardContent = z.looseObject({
    title: z.string().optional(),
    body: z.unknown().optional(),
});
export const LandingContent = z.looseObject({
    branch: z.string().optional(),
    summary: z.string().optional(),
    detail: z.string().optional(),
    step: z.string().optional(),
    error: z.string().optional(),
    result: z.unknown().optional(),
});
export const BuildContent = z.looseObject({ record: z.unknown() });
export const MachineStatusContent = z.looseObject({
    doctor: z.unknown().optional(),
    blockers: z.unknown().optional(),
});
export const RunMetaContent = z.looseObject({
    title: z.string().optional(),
    cwd: z.string().optional(),
    path: z.string().optional(),
    gitRoot: z.string().optional(),
    worktree: z.string().optional(),
    gitBranch: z.string().optional(),
    firstPrompt: z.string().optional(),
    model: z.string().optional(),
    cliVersion: z.string().optional(),
    entrypoint: z.string().optional(),
});
/** Artifact manifest metadata that never leaves the endpoint in plaintext. */
export const ArtifactMetaContent = z.looseObject({
    name: z.string(),
    mime: z.string().optional(),
    title: z.string().optional(),
    caption: z.string().optional(),
    group: z.string().optional(),
    sha256: z.string().optional(),
    bytes: z.number().int().nonnegative().optional(),
});
export const CommandResultContent = z.looseObject({
    result: z.unknown().optional(),
    error: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------
// Seal refs (`ns`, `kind`) — the single source of truth for every sealed location
// ---------------------------------------------------------------------------------------------
/** Entity kinds with a sealed content revision (`ref = {ns: kind, id, rev, kind}`). */
export const SEALED_ENTITY_KINDS = [
    'project',
    'workstream',
    'task',
    'rule',
    'idea',
    'card',
    'landing',
    'build',
    'machine',
    'hygiene',
    'handoff',
    'feedback',
    'run.meta',
    'artifact.meta',
    'brain.result',
    'release.request',
];
export const SealedEntityKindSchema = z.enum(SEALED_ENTITY_KINDS);
/** A sealed entity revision as carried in request/response bodies. */
export const SealedRevision = z.strictObject({ rev: Counter, sealed: Sealed });
export const Counters = { Counter, Count };
//# sourceMappingURL=e2e.js.map